import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Dog, Cat, ArrowLeft, Save, Search, X } from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

const NewPatient = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [breedSearch, setBreedSearch] = useState('');
  const [breedDropdownOpen, setBreedDropdownOpen] = useState(false);
  const breedRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    species: '',
    breed: '',
    age: '',
    weight: '',
    sex: 'unknown',
    owner_name: '',
    presenting_complaint: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/cases/patients`,
        formData,
        { withCredentials: true }
      );
      toast.success('Patient created successfully');
      navigate(`/cases/${data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create patient');
    } finally {
      setLoading(false);
    }
  };

  const commonDogBreeds = [
    'Mixed Breed',
    'Affenpinscher', 'Afghan Hound', 'Airedale Terrier', 'Akita', 'Alaskan Klee Kai',
    'Alaskan Malamute', 'American Bulldog', 'American English Coonhound',
    'American Eskimo Dog', 'American Foxhound', 'American Hairless Terrier',
    'American Leopard Hound', 'American Staffordshire Terrier', 'American Water Spaniel',
    'Anatolian Shepherd Dog', 'Appenzeller Sennenhund', 'Australian Cattle Dog',
    'Australian Kelpie', 'Australian Shepherd', 'Australian Stumpy Tail Cattle Dog',
    'Australian Terrier', 'Azawakh',
    'Barbado da Terceira', 'Barbet', 'Basenji', 'Basset Fauve de Bretagne',
    'Basset Hound', 'Bavarian Mountain Scent Hound', 'Beagle', 'Bearded Collie',
    'Beauceron', 'Bedlington Terrier', 'Belgian Laekenois', 'Belgian Malinois',
    'Belgian Sheepdog', 'Belgian Tervuren', 'Bergamasco Sheepdog', 'Berger Picard',
    'Bernese Mountain Dog', 'Bichon Frise', 'Biewer Terrier', 'Black and Tan Coonhound',
    'Black Russian Terrier', 'Bloodhound', 'Blue Picardy Spaniel', 'Bluetick Coonhound',
    'Boerboel', 'Bohemian Shepherd', 'Bolognese', 'Border Collie', 'Border Terrier',
    'Borzoi', 'Boston Terrier', 'Bouvier des Ardennes', 'Bouvier des Flandres',
    'Boxer', 'Boykin Spaniel', 'Bracco Italiano', 'Braque du Bourbonnais',
    'Braque Francais Pyrenean', 'Braque Saint-Germain', 'Brazilian Terrier',
    'Briard', 'Brittany', 'Broholmer', 'Brussels Griffon', 'Bull Terrier',
    'Bulldog', 'Bullmastiff',
    'Cairn Terrier', 'Calupoh', 'Canaan Dog', 'Canadian Eskimo Dog', 'Cane Corso',
    'Cardigan Welsh Corgi', 'Carolina Dog', 'Catahoula Leopard Dog',
    'Caucasian Shepherd Dog', 'Cavalier King Charles Spaniel',
    'Central Asian Shepherd Dog', 'Cesky Terrier', 'Chesapeake Bay Retriever',
    'Chihuahua', 'Chinese Crested', 'Chinese Shar-Pei', 'Chinook', 'Chow Chow',
    'Cirneco dell\'Etna', 'Clumber Spaniel', 'Cocker Spaniel', 'Collie',
    'Coton de Tulear', 'Croatian Sheepdog', 'Curly-Coated Retriever',
    'Czechoslovakian Vlciak',
    'Dachshund', 'Dalmatian', 'Dandie Dinmont Terrier', 'Danish-Swedish Farmdog',
    'Deutscher Wachtelhund', 'Doberman Pinscher', 'Dogo Argentino',
    'Dogue de Bordeaux', 'Drentsche Patrijshond', 'Drever', 'Dutch Shepherd',
    'English Cocker Spaniel', 'English Foxhound', 'English Setter',
    'English Springer Spaniel', 'English Toy Spaniel', 'Entlebucher Mountain Dog',
    'Estrela Mountain Dog', 'Eurasier',
    'Field Spaniel', 'Finnish Lapphund', 'Finnish Spitz', 'Flat-Coated Retriever',
    'French Bulldog', 'French Spaniel',
    'German Longhaired Pointer', 'German Pinscher', 'German Shepherd Dog',
    'German Shorthaired Pointer', 'German Spitz', 'German Wirehaired Pointer',
    'Giant Schnauzer', 'Glen of Imaal Terrier', 'Golden Retriever', 'Gordon Setter',
    'Grand Basset Griffon Vendeen', 'Great Dane', 'Great Pyrenees',
    'Greater Swiss Mountain Dog', 'Greyhound',
    'Hamiltonstovare', 'Hanoverian Scenthound', 'Harrier', 'Havanese',
    'Hokkaido', 'Hovawart',
    'Ibizan Hound', 'Icelandic Sheepdog', 'Irish Red and White Setter',
    'Irish Setter', 'Irish Terrier', 'Irish Water Spaniel', 'Irish Wolfhound',
    'Italian Greyhound',
    'Jagdterrier', 'Japanese Akitainu', 'Japanese Chin', 'Japanese Spitz',
    'Japanese Terrier',
    'Kai Ken', 'Karelian Bear Dog', 'Keeshond', 'Kerry Blue Terrier', 'Kishu Ken',
    'Komondor', 'Korean Jindo Dog', 'Kromfohrlander', 'Kuvasz',
    'Labrador Retriever', 'Lagotto Romagnolo', 'Lakeland Terrier',
    'Lancashire Heeler', 'Lapponian Herder', 'Large Munsterlander', 'Leonberger',
    'Lhasa Apso', 'Lowchen',
    'Maltese', 'Manchester Terrier (Standard)', 'Manchester Terrier (Toy)',
    'Mastiff', 'Miniature American Shepherd', 'Miniature Bull Terrier',
    'Miniature Pinscher', 'Miniature Schnauzer', 'Mountain Cur', 'Mudi',
    'Neapolitan Mastiff', 'Nederlandse Kooikerhondje', 'Newfoundland',
    'Norfolk Terrier', 'Norrbottenspets', 'Norwegian Buhund', 'Norwegian Elkhound',
    'Norwegian Lundehund', 'Norwich Terrier', 'Nova Scotia Duck Tolling Retriever',
    'Old English Sheepdog', 'Otterhound',
    'Papillon', 'Parson Russell Terrier', 'Pekingese', 'Pembroke Welsh Corgi',
    'Peruvian Inca Orchid', 'Petit Basset Griffon Vendeen', 'Pharaoh Hound',
    'Plott Hound', 'Pointer', 'Polish Lowland Sheepdog', 'Pomeranian',
    'Pont-Audemer Spaniel', 'Poodle (Miniature)', 'Poodle (Standard)',
    'Poodle (Toy)', 'Porcelaine', 'Portuguese Podengo',
    'Portuguese Podengo Pequeno', 'Portuguese Pointer', 'Portuguese Sheepdog',
    'Portuguese Water Dog', 'Presa Canario', 'Pudelpointer', 'Pug', 'Puli',
    'Pumi', 'Pyrenean Mastiff', 'Pyrenean Shepherd',
    'Rafeiro do Alentejo', 'Rat Terrier', 'Redbone Coonhound', 'Rhodesian Ridgeback',
    'Romanian Carpathian Shepherd', 'Romanian Mioritic Shepherd Dog', 'Rottweiler',
    'Russell Terrier', 'Russian Toy', 'Russian Tsvetnaya Bolonka',
    'Saint Bernard', 'Saluki', 'Samoyed', 'Schapendoes', 'Schipperke',
    'Scottish Deerhound', 'Scottish Terrier', 'Sealyham Terrier', 'Segugio Italiano',
    'Shetland Sheepdog', 'Shiba Inu', 'Shih Tzu', 'Shikoku Ken', 'Siberian Husky',
    'Silky Terrier', 'Skye Terrier', 'Sloughi', 'Slovakian Wirehaired Pointer',
    'Slovensky Cuvac', 'Slovensky Kopov', 'Small Munsterlander', 'Smooth Fox Terrier',
    'Soft Coated Wheaten Terrier', 'Spanish Mastiff', 'Spanish Water Dog',
    'Spinone Italiano', 'Stabyhoun', 'Staffordshire Bull Terrier',
    'Standard Schnauzer', 'Sussex Spaniel', 'Swedish Lapphund', 'Swedish Vallhund',
    'Taiwan Dog', 'Teddy Roosevelt Terrier', 'Thai Bangkaew', 'Thai Ridgeback',
    'Tibetan Mastiff', 'Tibetan Spaniel', 'Tibetan Terrier', 'Tornjak', 'Tosa',
    'Toy Fox Terrier', 'Transylvanian Hound', 'Treeing Tennessee Brindle',
    'Treeing Walker Coonhound',
    'Vizsla', 'Volpino Italiano',
    'Weimaraner', 'Welsh Springer Spaniel', 'Welsh Terrier',
    'West Highland White Terrier', 'Wetterhoun', 'Whippet', 'Wire Fox Terrier',
    'Wirehaired Pointing Griffon', 'Wirehaired Vizsla', 'Working Kelpie',
    'Xoloitzcuintli',
    'Yakutian Laika', 'Yorkshire Terrier',
    'Other'
  ];

  const commonCatBreeds = [
    'Domestic Shorthair', 'Domestic Longhair', 'Siamese', 'Persian',
    'Maine Coon', 'Ragdoll', 'British Shorthair', 'Abyssinian', 'Bengal',
    'Scottish Fold', 'Sphynx', 'Russian Blue', 'Birman'
  ];

  const breeds = formData.species === 'dog' ? commonDogBreeds : 
                 formData.species === 'cat' ? commonCatBreeds : [];

  const filteredBreeds = breeds.filter(b => 
    b.toLowerCase().includes(breedSearch.toLowerCase())
  );

  useEffect(() => {
    const handler = (e) => {
      if (breedRef.current && !breedRef.current.contains(e.target)) {
        setBreedDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="max-w-2xl mx-auto animate-fade-in" data-testid="new-patient-page">
      {/* Header */}
      <div className="mb-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/cases')}
          className="text-[#5C5F5C] hover:text-[#2A2C2A] mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Cases
        </Button>
        <h1 className="text-3xl font-semibold text-[#2A2C2A] font-['Work_Sans']">New Patient</h1>
        <p className="text-[#5C5F5C] mt-1">Enter patient information to create a new case</p>
      </div>

      <Card className="border-[#E5E4E0] bg-white">
        <CardHeader>
          <CardTitle className="text-lg font-['Work_Sans']">Patient Information</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Species Selection */}
            <div className="space-y-2">
              <Label className="text-[#2A2C2A]">Species *</Label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => handleChange('species', 'dog')}
                  className={`p-4 rounded-lg border-2 transition-all duration-200 flex items-center justify-center gap-3 ${
                    formData.species === 'dog'
                      ? 'border-[#C06144] bg-[#C06144]/10'
                      : 'border-[#E5E4E0] hover:border-[#C06144]/50'
                  }`}
                  data-testid="species-dog"
                >
                  <Dog className={`w-6 h-6 ${formData.species === 'dog' ? 'text-[#C06144]' : 'text-[#5C5F5C]'}`} />
                  <span className={formData.species === 'dog' ? 'text-[#C06144] font-medium' : 'text-[#5C5F5C]'}>Dog</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleChange('species', 'cat')}
                  className={`p-4 rounded-lg border-2 transition-all duration-200 flex items-center justify-center gap-3 ${
                    formData.species === 'cat'
                      ? 'border-[#4B6651] bg-[#4B6651]/10'
                      : 'border-[#E5E4E0] hover:border-[#4B6651]/50'
                  }`}
                  data-testid="species-cat"
                >
                  <Cat className={`w-6 h-6 ${formData.species === 'cat' ? 'text-[#4B6651]' : 'text-[#5C5F5C]'}`} />
                  <span className={formData.species === 'cat' ? 'text-[#4B6651] font-medium' : 'text-[#5C5F5C]'}>Cat</span>
                </button>
              </div>
            </div>

            {/* Patient Name */}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#2A2C2A]">Patient Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., Max"
                required
                className="bg-[#F8F7F4] border-[#E5E4E0] focus:ring-[#4B6651]/30 focus:border-[#4B6651]"
                data-testid="patient-name"
              />
            </div>

            {/* Breed */}
            <div className="space-y-2">
              <Label htmlFor="breed" className="text-[#2A2C2A]">Breed *</Label>
              <div className="relative" ref={breedRef}>
                <div
                  className="flex items-center gap-2 bg-[#F8F7F4] border border-[#E5E4E0] rounded-md px-3 h-10 cursor-pointer focus-within:ring-2 focus-within:ring-[#4B6651]/30 focus-within:border-[#4B6651]"
                  onClick={() => formData.species && setBreedDropdownOpen(true)}
                  data-testid="breed-select"
                >
                  <Search className="w-4 h-4 text-[#5C5F5C] flex-shrink-0" />
                  <input
                    type="text"
                    value={breedDropdownOpen ? breedSearch : formData.breed}
                    onChange={(e) => {
                      setBreedSearch(e.target.value);
                      setBreedDropdownOpen(true);
                    }}
                    onFocus={() => formData.species && setBreedDropdownOpen(true)}
                    placeholder={formData.species ? "Search breed..." : "Select species first"}
                    disabled={!formData.species}
                    className="flex-1 bg-transparent border-none outline-none text-sm text-[#2A2C2A] placeholder:text-[#5C5F5C]"
                    data-testid="breed-search-input"
                  />
                  {formData.breed && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleChange('breed', '');
                        setBreedSearch('');
                      }}
                      className="text-[#5C5F5C] hover:text-[#2A2C2A]"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {breedDropdownOpen && filteredBreeds.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-[#E5E4E0] rounded-md shadow-lg max-h-60 overflow-y-auto" data-testid="breed-dropdown">
                    {filteredBreeds.map(breed => (
                      <button
                        key={breed}
                        type="button"
                        onClick={() => {
                          handleChange('breed', breed);
                          setBreedSearch('');
                          setBreedDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-[#4B6651]/10 transition-colors ${
                          formData.breed === breed ? 'bg-[#4B6651]/10 text-[#4B6651] font-medium' : 'text-[#2A2C2A]'
                        }`}
                        data-testid={`breed-option-${breed.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        {breed}
                      </button>
                    ))}
                  </div>
                )}
                {breedDropdownOpen && breedSearch && filteredBreeds.length === 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-[#E5E4E0] rounded-md shadow-lg p-4 text-center">
                    <p className="text-sm text-[#5C5F5C]">No breeds matching "{breedSearch}"</p>
                    <button
                      type="button"
                      onClick={() => {
                        handleChange('breed', breedSearch);
                        setBreedDropdownOpen(false);
                        setBreedSearch('');
                      }}
                      className="mt-2 text-sm text-[#4B6651] hover:underline"
                      data-testid="use-custom-breed"
                    >
                      Use "{breedSearch}" as breed
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Age, Weight, Sex */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="age" className="text-[#2A2C2A]">Age *</Label>
                <Input
                  id="age"
                  value={formData.age}
                  onChange={(e) => handleChange('age', e.target.value)}
                  placeholder="e.g., 5 years"
                  required
                  className="bg-[#F8F7F4] border-[#E5E4E0] focus:ring-[#4B6651]/30 focus:border-[#4B6651]"
                  data-testid="patient-age"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="weight" className="text-[#2A2C2A]">Weight *</Label>
                <Input
                  id="weight"
                  value={formData.weight}
                  onChange={(e) => handleChange('weight', e.target.value)}
                  placeholder="e.g., 25 kg"
                  required
                  className="bg-[#F8F7F4] border-[#E5E4E0] focus:ring-[#4B6651]/30 focus:border-[#4B6651]"
                  data-testid="patient-weight"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#2A2C2A]">Sex</Label>
                <Select value={formData.sex} onValueChange={(v) => handleChange('sex', v)}>
                  <SelectTrigger className="bg-[#F8F7F4] border-[#E5E4E0] focus:ring-[#4B6651]/30 focus:border-[#4B6651]" data-testid="patient-sex">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male (Intact)</SelectItem>
                    <SelectItem value="female">Female (Intact)</SelectItem>
                    <SelectItem value="castrated">Male (Castrated)</SelectItem>
                    <SelectItem value="spayed">Female (Spayed)</SelectItem>
                    <SelectItem value="unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Owner Name */}
            <div className="space-y-2">
              <Label htmlFor="owner_name" className="text-[#2A2C2A]">Owner Name *</Label>
              <Input
                id="owner_name"
                value={formData.owner_name}
                onChange={(e) => handleChange('owner_name', e.target.value)}
                placeholder="e.g., John Smith"
                required
                className="bg-[#F8F7F4] border-[#E5E4E0] focus:ring-[#4B6651]/30 focus:border-[#4B6651]"
                data-testid="owner-name"
              />
            </div>

            {/* Presenting Complaint */}
            <div className="space-y-2">
              <Label htmlFor="presenting_complaint" className="text-[#2A2C2A]">Presenting Complaint *</Label>
              <Textarea
                id="presenting_complaint"
                value={formData.presenting_complaint}
                onChange={(e) => handleChange('presenting_complaint', e.target.value)}
                placeholder="Describe the chief complaint and history..."
                required
                rows={4}
                className="bg-[#F8F7F4] border-[#E5E4E0] focus:ring-[#4B6651]/30 focus:border-[#4B6651]"
                data-testid="presenting-complaint"
              />
            </div>

            {/* Submit */}
            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/cases')}
                className="border-[#E5E4E0]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#4B6651] hover:bg-[#3E5543] text-white flex-1"
                disabled={loading || !formData.species || !formData.name || !formData.breed}
                data-testid="submit-patient"
              >
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Creating...' : 'Create Patient'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default NewPatient;
